import { useQuery, useMutation, useQueryClient } from 'react-query'

import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'
import { useErrorHandler } from '@hapana/shared/web/hooks/errors.hooks'
import useAuthStore from '_stores/auth.store'
import {
  getTrialStatus,
  getOnboardingProgress,
  completeOnboardingStepFn,
  skipOnboardingStepFn,
  createCheckoutSessionFn,
  requestUpgradeFn,
  getVerifyCheckoutSession,
  TRIAL_STATUS_QUERY_KEY,
  ONBOARDING_PROGRESS_QUERY_KEY,
} from '_services/trial-management.service'

export const useTrialStatus = (corporateId: string | undefined) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const { user, idToken, headersReady } = useAuthStore()

  return useQuery([getTrialStatus.key, { corporateId }], getTrialStatus.query, {
    enabled: !!corporateId && !!user && !!idToken && headersReady,
    onError,
    select: (data: any) => data?.trialStatus || null,
    staleTime: 60_000,
    keepPreviousData: true,
  })
}

export const useOnboardingProgress = (corporateId: string | undefined) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const { user, idToken, headersReady } = useAuthStore()

  return useQuery([getOnboardingProgress.key, { corporateId }], getOnboardingProgress.query, {
    enabled: !!corporateId && !!user && !!idToken && headersReady,
    onError,
    select: (data: any) => data?.onboardingProgress || null,
    staleTime: 60_000,
  })
}

export const useCompleteOnboardingStep = () => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const queryClient = useQueryClient()

  return useMutation(completeOnboardingStepFn, {
    onError,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        TRIAL_STATUS_QUERY_KEY,
        { corporateId: variables.corporateId },
      ])
      queryClient.invalidateQueries([
        ONBOARDING_PROGRESS_QUERY_KEY,
        { corporateId: variables.corporateId },
      ])
    },
  })
}

export const useSkipOnboardingStep = () => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const queryClient = useQueryClient()

  return useMutation(skipOnboardingStepFn, {
    onError,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        TRIAL_STATUS_QUERY_KEY,
        { corporateId: variables.corporateId },
      ])
      queryClient.invalidateQueries([
        ONBOARDING_PROGRESS_QUERY_KEY,
        { corporateId: variables.corporateId },
      ])
    },
  })
}

export const useRequestUpgrade = () => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useMutation(requestUpgradeFn, {
    onError,
  })
}

export const useCreateCheckoutSession = () => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useMutation(createCheckoutSessionFn, {
    onError,
  })
}

export const useVerifyCheckoutSession = (sessionId: string | null) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const { user, idToken, headersReady } = useAuthStore()

  return useQuery([getVerifyCheckoutSession.key, { sessionId }], getVerifyCheckoutSession.query, {
    enabled: !!sessionId && !!user && !!idToken && headersReady,
    onError,
    select: (data: any) => data?.verifyCheckoutSession || null,
  })
}
